/**
 * Safari среди тех, кто выдаёт себя за всех сразу: Chrome, Edge и мобильные обёртки тоже пишут
 * `Safari` в user-agent, поэтому одного вхождения мало.
 */
export const isSafariUserAgent = (userAgent: string): boolean =>
  /safari/i.test(userAgent) &&
  !/(chrome|chromium|crios|fxios|edgios|edg\/|opr\/|android)/i.test(userAgent);

/**
 * Что просим у браузера при захвате экрана.
 *
 * Везде картинку имеет смысл ограничить: монитор 5K, отданный кодеку целиком, съедает процессор
 * и полосу. Но Safari ограничивать нельзя — WebKit при захвате экрана эти констрейнты не
 * принимает, и запрос отбивается вместо того, чтобы показать окно выбора.
 */
export const screenShareConstraints = (userAgent: string): DisplayMediaStreamOptions =>
  isSafariUserAgent(userAgent)
    ? { audio: false, video: true }
    : {
        audio: false,
        video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: 30 },
      };

/**
 * Есть ли прямо сейчас «живой жест».
 *
 * `getDisplayMedia` — единственный из медиа-запросов, который его требует: микрофон и камеру
 * браузер отдаёт и без него, поэтому работающая камера ничего не говорит о состоянии жеста. Без
 * активации Safari отказывает молча, даже не рисуя окно выбора, и снаружи это выглядит как
 * «кнопка ничего не делает». Значение уходит в отчёт об ошибке: оно отделяет потерянный жест от
 * настоящего отказа человека или системы.
 */
export const userActivationState = (): string => {
  if (typeof navigator === 'undefined' || !('userActivation' in navigator)) {
    return 'unknown';
  }
  const activation = navigator.userActivation;
  return `active=${activation.isActive} everActive=${activation.hasBeenActive}`;
};

/**
 * Умеет ли браузер захватывать экран вообще.
 *
 * <p>`getDisplayMedia` есть не везде: WebView, старые движки и часть сборок Safari его не
 * объявляют. Обращение к отсутствующему методу бросает `TypeError` СИНХРОННО — это не отказ
 * промиса, и обычный `try` вокруг `await` его не ловит, если сам вызов стоит выше по коду.
 * Так 05–06.09.2026 нажатие «Показать экран» в Safari роняло весь обработчик необработанным
 * отклонением: человек в живом звонке нажимал кнопку и не получал ни картинки, ни объяснения.
 */
export const isScreenShareSupported = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getDisplayMedia === 'function';
